import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  listGrupos,
  createGrupo,
  updateGrupo,
  deleteGrupo,
  getDeportistasByGrupo,
  assignDeportistasToGrupo,
} from "../../services/gruposService";
import { listCollaborators } from "../../services/collaboratorsService";
import { listDeportistas } from "../../services/deportistasService";
import { ModalGrupos } from "../../modals/ModalGrupos";
import Modal from "antd/es/modal/Modal";
import { Drawer, Checkbox, Skeleton } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";
import useAuth from "../../hooks/useAuth";

/** ===== UI Helpers ===== */
function clsx(...c) {
  return c.filter(Boolean).join(" ");
}

function Badge({ children, variant = "default" }) {
  const variants = {
    default: "bg-slate-100 text-slate-700 border-slate-200",
    success: "bg-emerald-50 text-emerald-700 border-emerald-200",
    danger: "bg-rose-50 text-rose-700 border-rose-200",
    muted: "bg-slate-50 text-slate-500 border-slate-200",
    info: "bg-blue-50 text-blue-700 border-blue-200",
  };
  
  const isStatusBadge = children === "Activo" || children === "Inactivo";
  const isActive = children === "Activo";
  
  return (
    <span
      className={clsx(
        "inline-flex items-center gap-1 rounded-full border text-xs px-2 py-1",
        variants[variant]
      )}
    >
      {isStatusBadge && (
        <span 
          className={clsx(
            "w-1.5 h-1.5 rounded-full",
            isActive ? "bg-emerald-500" : "bg-rose-500"
          )}
        />
      )}
      {children}
    </span>
  );
}

// === Íconos inline ===
function InboxIcon(props) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      {...props}
    >
      <path d="M22 12h-6l-2 3h-4l-2-3H2" />
      <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11Z" />
    </svg>
  );
}

/** ===== View ===== */
export default function AdminGroups() {
  const { user } = useAuth();
  const fallbackInstitution = "16020324-f0ea-4319-905c-5a30618ce9f5";
  const institutionId = user?.user_metadata?.institution_id || user?.user_metadata?.institucionId || fallbackInstitution;

  const [loading, setLoading] = useState(true);
  const [list, setList] = useState([]);
  const [query, setQuery] = useState("");
  const [onlyActive, setOnlyActive] = useState(true);
  const [viewMode, setViewMode] = useState("cards"); // "cards" | "list"
  
  // Modal estado
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  // Modal delete
  const [showDelete, setShowDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [targetDelete, setTargetDelete] = useState(null);

  // Modal deportistas
  const [showDeportistas, setShowDeportistas] = useState(false);
  const [deportistas, setDeportistas] = useState([]);
  const [loadingDeportistas, setLoadingDeportistas] = useState(false);
  const [selectedGrupo, setSelectedGrupo] = useState(null);

  // Drawer (Agregar deportistas)
  const [showDrawer, setShowDrawer] = useState(false);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [drawerDeportistas, setDrawerDeportistas] = useState([]);
  const [drawerQuery, setDrawerQuery] = useState("");
  const [selectedIds, setSelectedIds] = useState([]); // array of deportista ids to assign

  // Entrenadores
  const [entrenadores, setEntrenadores] = useState([]);

  // Mostrar solo activos en modal Deportistas (por defecto true)
  const [onlyActiveDeportistas, setOnlyActiveDeportistas] = useState(true);

  const [error, setError] = useState("");

  // Lista filtrada de deportistas según checkbox del modal
  const filteredDeportistas = useMemo(() => {
    if (!Array.isArray(deportistas)) return [];
    if (onlyActiveDeportistas) {
      return deportistas.filter((d) => d.activo !== false);
    }
    return deportistas;
  }, [deportistas, onlyActiveDeportistas]);

  // Función para cargar grupos (reutilizable)
  const loadGrupos = useCallback(async () => {
    if (!institutionId) return;
    
    try {
      setLoading(true);
      const data = await listGrupos(institutionId);
      const normalized = data.map(normalizeGroup);
      setList(normalized);
      setError("");
    } catch (err) {
      console.error("Error loading grupos:", err);
      setError(err?.message || "Error al cargar grupos");
      toast.error(err?.message || "Error al cargar grupos");
    } finally {
      setLoading(false);
    }
  }, [institutionId]);

  // Carga inicial: grupos
  useEffect(() => {
    loadGrupos();
  }, [loadGrupos]);

  // Carga inicial: entrenadores desde collaboratorsService
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const colabs = await listCollaborators?.();
        if (!mounted) return;

        // Filtro tolerante para “entrenador”
        const COACH_ROLE_ID = "1b6632eb-5967-4493-ae24-8ed9e29d590d"; // si aplica en tu backend
        const onlyCoaches = (colabs || []).filter((c) => {
          const roleId = c.rol_id || c.role_id || c.rol?.id || c.role?.id;
          const roleName =
            (c.rol_nombre || c.role_name || c.rol?.nombre || c.role?.name || "")
              .toString()
              .toLowerCase();
          return (
            roleId === COACH_ROLE_ID ||
            roleName.includes("entrenador") ||
            roleName.includes("coach")
          );
        });

        setEntrenadores(onlyCoaches);
      } catch (e) {
        console.error(e);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  // Búsqueda y filtro por activos (case-insensitive)
  const filtered = useMemo(() => {
    let result = list;

    // Filtrar por activos
    if (onlyActive) {
      result = result.filter((g) => g.activo);
    }

    // Búsqueda por nombre, categoría o entrenador
    const term = (query || "").toString().trim().toLowerCase();
    if (term) {
      result = result.filter((g) => {
        const nombre = (g.nombre || "").toString().toLowerCase();
        const categoria = (g.categoria || "").toString().toLowerCase();
        const entrenadorNombre = (g.entrenador_nombre || "").toString().toLowerCase();
        const entrenadorApellido = (g.entrenador_apellido || "").toString().toLowerCase();
        const asistenteNombre = (g.asistente_nombre || "").toString().toLowerCase();
        const asistenteApellido = (g.asistente_apellido || "").toString().toLowerCase();
        const fullSearch = `${nombre} ${categoria} ${entrenadorNombre} ${entrenadorApellido}`;
            return fullSearch.includes(term) || `${asistenteNombre} ${asistenteApellido}`.includes(term);
      });
    }

    return result;
  }, [list, query, onlyActive]);

  // ============================================================================
  // HANDLERS
  // ============================================================================

  function openCreate() {
    setEditing(null);
    setShowForm(true);
  }

  function openEdit(group) {
    setEditing(group);
    setShowForm(true);
  }

  function askDelete(group) {
    setTargetDelete(group);
    setShowDelete(true);
  }

  function closeModal() {
    setShowForm(false);
    setShowDelete(false);
    setShowDeportistas(false);
    setEditing(null);
    setTargetDelete(null);
    setSelectedGrupo(null);
    setSaving(false);
  }

  async function handleViewAthletes(group) {
    setSelectedGrupo(group);
    setShowDeportistas(true);
    setLoadingDeportistas(true);
    
    try {
      const data = await getDeportistasByGrupo(group.id);
      setDeportistas(data);
    } catch (err) {
      console.error("Error loading deportistas:", err);
      toast.error(err?.message || "Error al cargar deportistas");
      setDeportistas([]);
    } finally {
      setLoadingDeportistas(false);
    }
  }

  // Abrir drawer para agregar deportistas (carga deportistas activos)
  async function openAgregarDrawer() {
    setShowDrawer(true);
    setDrawerLoading(true);
    try {
      if (!selectedGrupo) {
        toast.error("Selecciona primero un grupo");
        setDrawerDeportistas([]);
        setDrawerQuery("");
        setSelectedIds([]);
        return;
      }

      const data = await listDeportistas();
      // Mostrar solo activos
      const activos = Array.isArray(data) ? data.filter((d) => d.activo !== false) : [];
      setDrawerDeportistas(activos);
      setDrawerQuery("");
      setSelectedIds([]);
    } catch (err) {
      console.error("Error loading deportistas for drawer:", err);
      toast.error(err?.message || "Error al cargar deportistas");
      setDrawerDeportistas([]);
    } finally {
      setDrawerLoading(false);
    }
  }

  const filteredDrawerDeportistas = useMemo(() => {
    const q = (drawerQuery || "").toString().trim().toLowerCase();

    // Helper: determine if a deportista already belongs to the selected group
    const isInSelectedGroup = (d) => {
      if (!selectedGrupo) return false;
      const selectedId = selectedGrupo.id;
      const selectedName = (selectedGrupo.nombre || "").toString().trim().toLowerCase();

      const grupoId = d.grupo?.id || d.grupo_id || d.grupoId || null;
      const grupoName = (d.grupo?.nombre || d.grupo_nombre || "").toString().trim().toLowerCase();

      if (grupoId && selectedId && grupoId === selectedId) return true;
      if (grupoName && selectedName && grupoName === selectedName) return true;
      return false;
    };

    return drawerDeportistas.filter((d) => {
      // Exclude deportistas that already belong to the selected group
      if (isInSelectedGroup(d)) return false;

      if (!q) return true;

      const text = `${d.nombres || ""} ${d.apellidos || ""} ${d.cedula || ""} ${d.email || ""} ${d.grupo?.nombre || d.grupo_nombre || ""}`.toLowerCase();
      return text.includes(q);
    });
  }, [drawerDeportistas, drawerQuery, selectedGrupo]);

  function toggleSelect(id) {
    setSelectedIds((prev) => {
      if (!prev) return [id];
      return prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
    });
  }

  async function handleSendAssign() {
    if (!selectedGrupo?.id) return toast.error("Grupo no seleccionado");
    if (!selectedIds || selectedIds.length === 0) {
      toast.error("Selecciona al menos un deportista");
      return;
    }

    try {
      setDrawerLoading(true);
      const payload = selectedIds.map((id) => ({ deportista_id: id }));
      await assignDeportistasToGrupo(selectedGrupo.id, payload);
      toast.success("Deportistas asignados correctamente");
      // refrescar lista de deportistas del grupo
      const refreshed = await getDeportistasByGrupo(selectedGrupo.id);
      setDeportistas(refreshed);
      setShowDrawer(false);
    } catch (err) {
      console.error("Assign error:", err);
      toast.error(err?.message || "Error al asignar deportistas");
    } finally {
      setDrawerLoading(false);
    }
  }

  async function onSubmit(dataForm) {
    if (!institutionId) return;

    // Validación mínima
    if (!dataForm.nombre?.trim()) {
      toast.error("El nombre es obligatorio");
      return;
    }
    if (!dataForm.entrenador_principal_id?.trim()) {
      toast.error("El entrenador es obligatorio");
      return;
    }

    setSaving(true);
    try {
      if (editing?.id) {
        // Editar
        await updateGrupo(editing.id, dataForm);
        await loadGrupos();
        toast.success("Grupo actualizado correctamente");
      } else {
        // Crear
        await createGrupo(dataForm, institutionId);
        await loadGrupos();
        toast.success("Grupo creado correctamente");
      }
      closeModal();
    } catch (e) {
      console.error("Save error:", e);
      toast.error(e?.message || "No se pudo guardar el grupo");
    } finally {
      setSaving(false);
    }
  }

  async function onConfirmDelete() {
    if (!targetDelete?.id) return;
    setDeleting(true);
    try {
      await deleteGrupo(targetDelete.id);
      await loadGrupos();
      toast.success("Grupo eliminado correctamente");
      closeModal();
    } catch (e) {
      console.error("Delete error:", e);
      toast.error(e?.message || "No se pudo eliminar el grupo");
    } finally {
      setDeleting(false);
    }
  }

  // ============================================================================
  // JSX RENDER
  // ============================================================================

  return (
    <div className="mx-auto max-w-7xl p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
        <div>
          <h1 className="text-xl md:text-2xl font-semibold">Grupos</h1>
          <p className="text-slate-600 text-sm">
            Gestión de grupos de la institución.
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row md:items-center gap-3 mb-6">
        <div className="flex-1">
          <input
            className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-4 focus:ring-blue-100"
            placeholder="Buscar por nombre, categoría o entrenador..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        
        {/* Custom Checkbox for Active Filter */}
        <label className="inline-flex items-center gap-2.5 px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm text-slate-700 select-none cursor-pointer hover:bg-slate-50 transition-colors">
          <div className="relative flex items-center">
            <input
              type="checkbox"
              checked={onlyActive}
              onChange={(e) => setOnlyActive(e.target.checked)}
              className="peer sr-only"
            />
            <div className="w-5 h-5 rounded border-2 border-slate-300 bg-white peer-checked:bg-blue-600 peer-checked:border-blue-600 transition-all flex items-center justify-center">
              {onlyActive && (
                <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              )}
            </div>
          </div>
          <span className="font-medium">Mostrar solo activos</span>
        </label>
        
        {/* View Mode Switch */}
        <div className="flex items-center gap-1 p-1 bg-slate-100 rounded-lg">
          <button
            onClick={() => setViewMode("cards")}
            className={clsx(
              "px-3 py-1.5 rounded-md text-sm font-medium transition-all",
              viewMode === "cards"
                ? "bg-white text-blue-600 shadow-sm"
                : "text-slate-600 hover:text-slate-900"
            )}
            title="Vista de tarjetas"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
            </svg>
          </button>
          <button
            onClick={() => setViewMode("list")}
            className={clsx(
              "px-3 py-1.5 rounded-md text-sm font-medium transition-all",
              viewMode === "list"
                ? "bg-white text-blue-600 shadow-sm"
                : "text-slate-600 hover:text-slate-900"
            )}
            title="Vista de lista"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h.01M4 12h.01M4 18h.01M8 6h13M8 12h13M8 18h13" />
            </svg>
          </button>
        </div>
        
        <button
          onClick={loadGrupos}
          disabled={loading}
          className="inline-flex items-center gap-2 rounded-xl bg-slate-100 text-slate-700 px-3 py-2 text-sm font-medium hover:bg-slate-200 active:scale-[.99] transition disabled:opacity-50 disabled:cursor-not-allowed"
          title="Recargar datos"
        >
          <ReloadOutlined className="text-base" spin={loading} />
        </button>
        
        <button
          onClick={openCreate}
          className="inline-flex items-center gap-2 rounded-xl bg-blue-600 text-white px-4 py-2 text-sm font-medium hover:bg-blue-700 active:scale-[.99] transition"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Nuevo grupo
        </button>
      </div>

      {error && (
        <div className="mb-4 rounded-xl border border-rose-200 bg-rose-50 text-rose-700 px-3 py-2 text-sm">
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div
              key={i}
              className="h-80 rounded-2xl border border-slate-200 bg-white overflow-hidden"
            >
              <div className="animate-pulse h-full bg-slate-100" />
            </div>
          ))}
        </div>
      )}

      {/* Cards View */}
      {!loading && viewMode === "cards" && (
        <>
          {filtered.length === 0 ? (
            <div className="text-center py-16 px-4">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-slate-100 mb-4">
                <InboxIcon className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-1">No se encontraron grupos</h3>
              <p className="text-sm text-slate-500">
                {query ? "Intenta ajustar los filtros de búsqueda" : "No hay grupos registrados"}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filtered.map((g) => (
                <article
                  key={g.id}
                  className="flex flex-col overflow-hidden rounded-2xl shadow-sm border border-slate-200 bg-white hover:shadow-md transition"
                >
                  <div className="p-4">
                    {/* Header con botones de acción */}
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-base font-semibold truncate">
                          {g.nombre}
                        </h3>
                        <div className="mt-1 flex items-center gap-2 flex-wrap">
                          <Badge variant={g.activo ? "success" : "danger"}>
                            {g.activo ? "Activo" : "Inactivo"}
                          </Badge>
                          <Badge variant="info">{g.categoria || "Sin categoría"}</Badge>
                        </div>
                      </div>
                      
                      {/* Botones de acción en esquina */}
                      <div className="flex gap-1">
                        {g.activo && (
                          <button
                            onClick={() => openEdit(g)}
                            className="rounded-lg border border-slate-200 hover:bg-slate-100 p-2 transition"
                            title="Editar grupo"
                            aria-label="Editar"
                          >
                            <svg className="w-4 h-4 text-slate-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                        )}
                        {g.activo && (
                          <button
                            onClick={() => askDelete(g)}
                            className="rounded-lg border border-slate-200 hover:bg-slate-100 p-2 transition"
                            title="Eliminar grupo"
                            aria-label="Eliminar"
                          >
                            <svg className="w-4 h-4 text-rose-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Información del grupo */}
                    <div className="text-sm text-slate-700 grid grid-cols-2 gap-y-2 gap-x-3">
                      <span className="text-slate-500">Entrenador:</span>
                      <span className="truncate">
                        {g.entrenador_nombre && g.entrenador_apellido
                          ? `${g.entrenador_nombre} ${g.entrenador_apellido}`
                          : "—"}
                      </span>
                      <span className="text-slate-500">Asistente:</span>
                      <span className="truncate">
                        {g.asistente_nombre && g.asistente_apellido
                          ? `${g.asistente_nombre} ${g.asistente_apellido}`
                          : "—"}
                      </span>
                      <span className="text-slate-500">Rango edad:</span>
                      <span>{formatEdad(g.edad_minima, g.edad_maxima)} años</span>
                      <span className="text-slate-500 col-span-2">Horarios:</span>
                      <div className="col-span-2 text-xs space-y-0.5">
                        {["lunes", "martes", "miercoles", "jueves", "viernes", "sabado", "domingo"].map(
                          (d) =>
                            g.horarios?.[d] ? (
                              <div key={d} className="flex justify-between">
                                <span className="capitalize text-slate-500">{d}:</span>
                                <span>{g.horarios[d]}</span>
                              </div>
                            ) : null
                        )}
                      </div>
                    </div>

                    {/* Botón Ver deportistas */}
                    <button
                      onClick={() => handleViewAthletes(g)}
                      className="w-full inline-flex items-center justify-center rounded-xl border border-blue-600 text-blue-600 px-3 py-2 text-sm hover:bg-blue-50 hover:border-blue-400 hover:text-blue-500 active:scale-[.99] transition mt-4"
                    >
                      Ver deportistas
                    </button>
                  </div>
                </article>
              ))}
            </div>
          )}
        </>
      )}

      {/* List View (Table) */}
      {!loading && viewMode === "list" && (
          <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Nombre
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Categoría
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Entrenador
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Asistente
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Rango Edad
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Estado
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {filtered.map((g) => (
                    <tr
                      key={g.id}
                      className="hover:bg-slate-50 transition-colors"
                    >
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm font-medium text-slate-900">
                          {g.nombre}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-700">
                        {g.categoria || "—"}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-700">
                        {g.entrenador_nombre && g.entrenador_apellido
                          ? `${g.entrenador_nombre} ${g.entrenador_apellido}`
                          : "—"}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-700">
                        {g.asistente_nombre && g.asistente_apellido
                          ? `${g.asistente_nombre} ${g.asistente_apellido}`
                          : "—"}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-700">
                        {formatEdad(g.edad_minima, g.edad_maxima)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <Badge
                          variant={g.activo ? "success" : "danger"}
                        >
                          {g.activo ? "Activo" : "Inactivo"}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => openEdit(g)}
                            className="inline-flex items-center justify-center rounded-lg border border-blue-600 text-blue-600 px-3 py-1.5 text-xs font-medium hover:bg-blue-50 hover:border-blue-400 hover:text-blue-500 transition"
                            title="Editar grupo"
                          >
                            Editar
                          </button>
                          {g.activo && (
                            <button
                              onClick={() => askDelete(g)}
                              className="inline-flex items-center justify-center rounded-lg border border-rose-600 text-rose-600 px-3 py-1.5 text-xs font-medium hover:bg-rose-50 hover:border-rose-400 transition"
                              title="Eliminar grupo"
                            >
                              Eliminar
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Empty State for List View */}
            {filtered.length === 0 && (
              <div className="text-center py-12 text-slate-500">
                <p className="text-sm">No se encontraron grupos</p>
              </div>
            )}
          </div>
      )}

      {/* ===== Modal Form (Crear/Editar) ===== */}
      <ModalGrupos
        open={showForm}
        editing={editing}
        entrenadores={entrenadores}
        saving={saving}
        onClose={closeModal}
        onSubmit={onSubmit}
      />

      {/* ===== Modal Delete ===== */}
      <Modal
        open={showDelete}
        onCancel={() => {
          setShowDelete(false);
          setTargetDelete(null);
        }}
        title="Eliminar grupo"
        footer={null}
        centered
      >
        <p className="text-sm text-slate-600 mb-4">
          ¿Seguro que deseas eliminar el grupo{" "}
          <span className="font-medium text-slate-900">
            {targetDelete?.nombre}
          </span>
          ? Esta acción no se puede deshacer.
        </p>
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => {
              setShowDelete(false);
              setTargetDelete(null);
            }}
            className="rounded-xl border border-slate-300 px-4 py-2 text-sm hover:bg-slate-100 transition"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={onConfirmDelete}
            disabled={deleting}
            className="rounded-xl bg-rose-600 text-white px-4 py-2 text-sm hover:bg-rose-700 disabled:opacity-60 transition"
          >
            {deleting ? "Eliminando..." : "Eliminar"}
          </button>
        </div>
      </Modal>

      {/* Drawer para agregar deportistas al grupo */}
      <Drawer
        title={`Agregar deportistas a: ${selectedGrupo?.nombre || "-"}`}
        placement="right"
        onClose={() => setShowDrawer(false)}
        open={showDrawer}
        width={420}
      >
        {drawerLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="p-3 rounded-2xl bg-white border border-slate-100 shadow-sm">
                <Skeleton active avatar paragraph={{ rows: 1 }} />
              </div>
            ))}
          </div>
        ) : drawerDeportistas.length === 0 ? (
          <div className="py-8 text-center text-sm text-slate-600">No hay deportistas activos disponibles.</div>
        ) : (
          <div className="space-y-3 max-h-[70vh] overflow-y-auto">
            <div className="mb-3">
              <input
                value={drawerQuery}
                onChange={(e) => setDrawerQuery(e.target.value)}
                placeholder="Buscar deportista por nombre, apellido..."
                className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-4 focus:ring-blue-100"
              />
            </div>

            {filteredDrawerDeportistas.length === 0 ? (
              <div className="py-6 text-center text-sm text-slate-600">No hay deportistas que coincidan con la búsqueda.</div>
            ) : (
              filteredDrawerDeportistas.map((d) => {
                return (
                  <div key={d.id} className="flex items-center gap-3 p-3 rounded-2xl bg-white border border-slate-100 shadow-sm hover:shadow-md transition">
                    <div>
                      <Checkbox checked={selectedIds?.includes(d.id)} onChange={() => toggleSelect(d.id)} />
                    </div>
                    <div className="flex items-center justify-center w-12 h-12 rounded-full bg-blue-50 flex-shrink-0">
                      <span className="text-blue-600 font-semibold text-lg">{(d.nombres||"")?.charAt(0)}{(d.apellidos||"")?.charAt(0)}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-3">
                        <div className="text-sm font-semibold text-slate-900 truncate">{d.nombres} {d.apellidos}</div>
                        <div className="flex items-center gap-2">
                          <div className="text-xs text-slate-500">{d.genero || "-"}</div>
                          <Badge variant="info">{d.grupo?.categoria ?? d.grupo_categoria ?? "Sin categoría"}</Badge>
                        </div>
                      </div>
                      <div className="mt-1 text-xs text-slate-600 flex items-center gap-4">
                        <span>Edad: <span className="font-medium text-slate-900">{calcularEdad(d.fecha_nacimiento)}</span></span>
                        <span>Grupo: <span className="font-medium text-slate-900">{d.grupo?.nombre ?? d.grupo_nombre ?? "-"}</span></span>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

        <div className="mt-6 border-t pt-4 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => setShowDrawer(false)}
            className="rounded-xl border border-slate-300 px-4 py-2 text-sm hover:bg-slate-100 transition"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSendAssign}
            disabled={drawerLoading}
            className="rounded-xl bg-blue-600 text-white px-4 py-2 text-sm hover:bg-blue-700 disabled:opacity-60 transition"
          >
            {drawerLoading ? "Enviando..." : "Enviar"}
          </button>
        </div>
      </Drawer>

      {/* Modal Deportistas del Grupo */}
      <Modal
        open={showDeportistas}
        onCancel={closeModal}
        title={
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-blue-100">
              <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-900">
                Deportistas del Grupo
              </h3>
              <p className="text-sm text-slate-500 font-normal">
                {selectedGrupo?.nombre} - {selectedGrupo?.categoria || "Sin categoría"}
              </p>
            </div>
          </div>
        }
        footer={null}
        centered
        width={800}
        className="deportistas-modal"
      >
        {loadingDeportistas ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className="flex items-start gap-3 p-4 rounded-xl border border-slate-200 bg-slate-50"
              >
                <div className="w-12 h-12 rounded-full bg-slate-200 animate-pulse flex-shrink-0"></div>
                <div className="flex-1 min-w-0 space-y-2">
                  <div className="h-8 bg-slate-200 rounded animate-pulse w-3/4"></div>
                  <div className="h-8 bg-slate-200 rounded animate-pulse w-1/2"></div>
                  <div className="h-8 bg-slate-200 rounded animate-pulse w-2/3"></div>
                  <div className="h-8 bg-slate-200 rounded animate-pulse w-1/3"></div>
                </div>
              </div>
            ))}
          </div>
        ) : filteredDeportistas.length === 0 ? (
          <div className="text-center py-12 px-4">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-slate-100 mb-4">
              <svg className="w-8 h-8 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-1">
              {onlyActiveDeportistas ? "No hay deportistas activos" : "No hay deportistas"}
            </h3>
            <p className="text-sm text-slate-500">
              {onlyActiveDeportistas
                ? "No hay deportistas activos en este grupo."
                : "Este grupo aún no tiene deportistas registrados."}
            </p>
          </div>
        ) : (
          <div className="max-h-[500px] overflow-y-auto">
            <div className="mb-3 px-1 flex items-center justify-between">
              <p className="text-sm text-slate-600">
                Total de deportistas: <span className="font-semibold text-slate-900">{filteredDeportistas.length}</span>
              </p>

              <label className="inline-flex items-center gap-2.5 px-2 py-1 rounded-lg border border-slate-200 bg-white text-sm text-slate-700 select-none cursor-pointer hover:bg-slate-50 transition">
                <div className="relative flex items-center">
                  <input
                    type="checkbox"
                    checked={onlyActiveDeportistas}
                    onChange={(e) => setOnlyActiveDeportistas(e.target.checked)}
                    className="peer sr-only"
                  />
                  <div className="w-4 h-4 rounded border-2 border-slate-300 bg-white peer-checked:bg-blue-600 peer-checked:border-blue-600 transition-all flex items-center justify-center">
                    {onlyActiveDeportistas && (
                      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                </div>
                <span className="font-medium">Mostrar solo activos</span>
              </label>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {filteredDeportistas.map((deportista) => (
                <div
                  key={deportista.id}
                  className="flex items-start gap-3 p-4 rounded-xl border border-slate-200 bg-slate-50 hover:bg-white hover:shadow-sm transition-all"
                >
                  <div className="flex items-center justify-center w-12 h-12 rounded-full bg-blue-100 flex-shrink-0">
                    <span className="text-blue-600 font-semibold text-lg">
                      {deportista.nombres?.charAt(0)}{deportista.apellidos?.charAt(0)}
                    </span>
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-semibold text-slate-900 truncate">
                      {deportista.nombres} {deportista.apellidos}
                    </h4>
                    
                    <div className="mt-1 space-y-1">
                      {deportista.cedula && (
                        <p className="text-xs text-slate-600 flex items-center gap-1">
                          <svg className="w-3.5 h-3.5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />
                          </svg>
                          <span>CI: {deportista.cedula}</span>
                        </p>
                      )}
                      
                      {deportista.email && (
                        <p className="text-xs text-slate-600 flex items-center gap-1">
                          <svg className="w-3.5 h-3.5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                          <span className="truncate">{deportista.email}</span>
                        </p>
                      )}
                      
                      {deportista.fecha_nacimiento && (
                        <p className="text-xs text-slate-600 flex items-center gap-1">
                          <svg className="w-3.5 h-3.5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          <span>Edad: {calcularEdad(deportista.fecha_nacimiento)} años</span>
                        </p>
                      )}
                      
                      {deportista.telefono && (
                        <p className="text-xs text-slate-600 flex items-center gap-1">
                          <svg className="w-3.5 h-3.5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                          </svg>
                          <span>{deportista.telefono}</span>
                        </p>
                      )}
                      
                      {deportista.posicion?.nombre && (
                        <p className="text-xs text-slate-600 flex items-center gap-1">
                          <svg className="w-3.5 h-3.5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                          </svg>
                          <span>Posición: {deportista.posicion.nombre}</span>
                        </p>
                      )}
                      
                      {(deportista.representante?.nombre || deportista.representante?.apellido) && (
                        <p className="text-xs text-slate-600 flex items-center gap-1">
                          <svg className="w-3.5 h-3.5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          <span className="truncate">
                            Representante: {deportista.representante.nombre} {deportista.representante.apellido}
                          </span>
                        </p>
                      )}
                    </div>
                    
                    {deportista.activo !== undefined && (
                      <div className="mt-2">
                        <Badge variant={deportista.activo ? "success" : "danger"}>
                          {deportista.activo ? "Activo" : "Inactivo"}
                        </Badge>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        <div className="flex justify-end mt-4 pt-4 border-t border-slate-200">
          <button
            type="button"
            onClick={openAgregarDrawer}
            disabled={!selectedGrupo}
            className="rounded-xl border border-blue-600 text-blue-600 px-4 py-2 text-sm font-medium hover:bg-blue-50 transition disabled:opacity-50 mr-2"
          >
            Agregar
          </button>
          <button
            type="button"
            onClick={closeModal}
            className="rounded-xl bg-slate-600 text-white px-4 py-2 text-sm font-medium hover:bg-slate-700 transition"
          >
            Cerrar
          </button>
        </div>
      </Modal>
    </div>
  );
}

/** ===== utils específicos ===== */
function normalizeGroup(g) {
  const edadMaxima = numberOrNull(g.edad_maxima ?? g.edad_max);
  
  return {
    id: g.id,
    entrenador_principal_id: g.entrenador_principal_id ?? "",
    entrenador_nombre: g.entrenador_principal?.nombres ?? g.entrenador_principal?.nombre ?? "",
    entrenador_apellido: g.entrenador_principal?.apellidos ?? g.entrenador_principal?.apellido ?? "",
    entrenador_secun_id: g.entrenador_secun_id ?? g.entrenador_secun ?? "",
    asistente_nombre: g.entrenador_auxiliar?.nombres ?? g.entrenador_auxiliar?.nombre ?? "",
    asistente_apellido: g.entrenador_auxiliar?.apellidos ?? g.entrenador_auxiliar?.apellido ?? "",
    nombre: g.nombre ?? "",
    categoria: g.categoria ?? "",
    edad_minima: numberOrNull(g.edad_minima),
    edad_maxima: edadMaxima,
    edad_max: edadMaxima, // Mantener compatibilidad
    descripcion: g.descripcion ?? "",
    horarios: {
      lunes: g.horarios?.lunes ?? "",
      martes: g.horarios?.martes ?? "",
      miercoles: g.horarios?.miercoles ?? "",
      jueves: g.horarios?.jueves ?? "",
      viernes: g.horarios?.viernes ?? "",
      sabado: g.horarios?.sabado ?? "",
      domingo: g.horarios?.domingo ?? "",
    },
    activo: g.activo ?? true,
  };
}

function numberOrNull(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function formatEdad(min, max) {
  if (min == null && max == null) return "—";
  if (min != null && max != null) return `${min}–${max}`;
  if (min != null) return `≥ ${min}`;
  return `≤ ${max}`;
}

function calcularEdad(fechaNacimiento) {
  if (!fechaNacimiento) return "—";
  const hoy = new Date();
  const nacimiento = new Date(fechaNacimiento);
  let edad = hoy.getFullYear() - nacimiento.getFullYear();
  const mes = hoy.getMonth() - nacimiento.getMonth();
  if (mes < 0 || (mes === 0 && hoy.getDate() < nacimiento.getDate())) {
    edad--;
  }
  return edad;
}
